import { checkForDeviceConflict } from '@weenat/client/dist/core/plots'
import { Device, DeviceAvailableMeasure } from '@weenat/client/dist/resources/devices'
import { Plot } from '@weenat/client/dist/resources/plots'
import { useIntl } from '@weenat/wintl'
import Card from 'app/src/kit/Card'
import Checkbox from 'app/src/kit/Checkbox'
import DelimitedFlex from 'app/src/kit/DelimitedFlex'
import Text from 'app/src/kit/Text'
import TextEllipsisContainer from 'app/src/kit/TextEllipsisContainer'
import WeenatPressable from 'app/src/kit/WeenatPressable'
import { useToggleFeature } from 'app/state'
import { isEmpty } from 'lodash-es'
import isNil from 'lodash-es/isNil'
import { styled } from 'styled-components'
import PlotOriginBadge from '../plots/PlotOriginBadge'

const Container = styled(WeenatPressable)`
  margin-bottom: ${(p) => p.theme.spacings.md}px;
`

interface LinkablePlotItemProps {
  plot: Plot
  isSelected: boolean
  onPress: () => void
  device: Device
}

const LinkablePlotItem: FC<LinkablePlotItemProps> = ({ isSelected, plot, onPress, device }) => {
  const [{ soil_temperature_widget }] = useToggleFeature()
  const { t } = useIntl()

  const plotDeviceMetricAggregatedByDeviceId: Map<number, DeviceAvailableMeasure[]> = new Map()

  plot.currentMeasurementConfigs.reduce((acc, measuremenConfig) => {
    if (!isNil(measuremenConfig.deviceId)) {
      if (!acc.has(measuremenConfig.deviceId)) {
        acc.set(measuremenConfig.deviceId, [measuremenConfig.metric])
      } else {
        acc.get(measuremenConfig.deviceId)?.push(measuremenConfig.metric)
      }
    }
    return acc
  }, plotDeviceMetricAggregatedByDeviceId)

  const conflicts = checkForDeviceConflict(
    [
      { id: device.id, availableMeasures: device.availableMeasures },
      ...Array.from(plotDeviceMetricAggregatedByDeviceId.entries()).map(([k, v]) => ({
        id: k,
        availableMeasures: v
      }))
    ],
    soil_temperature_widget
  )

  const hasConflict = !isEmpty(conflicts.duplications) || !isEmpty(conflicts.incompatibilities)

  return (
    <Container
      onPress={!hasConflict ? onPress : undefined}
      targetFirstChild
      isDisabled={hasConflict}
    >
      <Card $p={0} $borderRadius={'md'}>
        <DelimitedFlex
          $flexWrap='wrap'
          $gap='md'
          $backgroundColor={isSelected ? 'primary-200' : undefined}
          $p='md'
          $alignItems='center'
        >
          <Checkbox checked={isSelected} disabled={hasConflict} />
          <TextEllipsisContainer>
            <Text $ellipsis $color={isSelected ? 'primary.500' : undefined}>
              {plot.name}
            </Text>
          </TextEllipsisContainer>
          <PlotOriginBadge plot={plot} />
        </DelimitedFlex>
        <Box $p='md'>
          {!hasConflict ? (
            <Text $color='feedback.success.500'>{t('models.device.compatible_plot')}</Text>
          ) : !isEmpty(conflicts.duplications) ? (
            <Text $color='feedback.error.500'>
              {t('models.device.duplicated_metric_conflict_description', {
                metric: t(`metrics.${conflicts.duplications.at(0)?.metric}` as const)
              })}
            </Text>
          ) : !isEmpty(conflicts.incompatibilities) ? (
            <Text $color='feedback.error.500'>
              {t('models.device.incompatible_metric_conflict_description', {
                metrics: conflicts.incompatibilities
                  .at(0)
                  ?.metrics.map((m) => t(`metrics.${m as const}`))
              })}
            </Text>
          ) : null}
        </Box>
      </Card>
    </Container>
  )
}

export default LinkablePlotItem
